import { App } from "vue";
import localforage from "localforage";

import { createDefaultLogger, LogLevels } from "@/types/console-logger";
const logger = createDefaultLogger( LogLevels.DEBUG );

/*******************************************************************************************************
 * DATABASE IDENTITY — READ THIS BEFORE CHANGING ANYTHING IN THIS FILE.
 *
 * `DATABASE_NAME` is FROZEN. Do not rename it. Ever.
 *
 * This database was renamed 34 times ('tmi' -> 'tmi-prime' -> ... -> 'sentient-v101010' -> ... -> 's').
 * Renaming an IndexedDB database does not migrate it — it orphans it. Every one of those renames
 * silently threw away every user's cached content and forced a full re-download, which is how
 * "reset your data" became the standard answer to any cache problem. The name is ugly; leaving it
 * ugly costs nothing, and renaming it now would repeat the exact mistake one final time.
 *
 * When the SHAPE of cached data changes, bump `CACHE_SCHEMA_VERSION` instead. That runs a real,
 * one-time migration against the existing database (see `applySchemaMigrations`) and leaves
 * everything the migration does not care about intact.
 *******************************************************************************************************/
const DATABASE_NAME: string = 's';

/** IndexedDB structural version. localforage bumps this itself when it adds an object store. */
const DATABASE_VERSION: number = 1;

/**
 * Version of the *shape* of the records we cache, tracked independently of the IndexedDB version.
 *
 * Bump this when a normalized record's structure changes in a way that makes previously cached
 * rows unreadable or wrong. `applySchemaMigrations` then clears the cached content once, on the
 * next load, and the sync coordinators repopulate it. Users keep their database, their settings
 * and their session — they just re-sync content.
 */
export const CACHE_SCHEMA_VERSION: number = 1;

/** Key under which the schema version is recorded, inside the `sync_state` store. */
export const cacheSchemaVersionKey: string = 'cache-schema-version';

/**
 * Databases from the pre-freeze era. These are dropped once per browser, not once per page load.
 *
 * Historical note: this used to be 33 `dropInstance()` calls fired unawaited on every app boot —
 * including from inside the sync worker, so two threads raced 66 deletes against 15 concurrent
 * opens on the same origin. `deleteDatabase` blocks while any connection is open, so with a
 * second tab open these blocked each other.
 */
const LEGACY_DATABASE_NAMES: readonly string[] = [
    'tmi', 'tmi-prime', 'tmi-se', 'tmi-sen', 'tmi-sent', 'tmi-senti', 'tmi-sentie', 'tmi-sentien',
    'sentient', 'sentient-', 'sentient-v', 'sentient-v1', 'sentient-v1.', 'sentient-v10',
    'sentient-v101', 'sentient-v1010', 'sentient-v10101', 'sentient-v101010', 'sentient-v1010101',
    'sentient-1010101', 'sentient-010101', 'sentient-10101', 'sentient-0101', 'sentient-101',
    'sentient-01', 'sentient-1', 'sentien', 'sentie', 'senti', 'sent', 'sen', 'se'
];

/**
 * Bump this if a future release needs the legacy purge to run again. The flag lives in
 * localStorage so the purge survives page loads instead of re-running on every one of them.
 */
const LEGACY_PURGE_GENERATION: string = '1';
const legacyPurgeFlagKey: string = 'tmi.legacy-idb-purge';

/** A blocked `deleteDatabase` never settles. Never let cleanup hold a promise open forever. */
const DROP_TIMEOUT_IN_MILLISECONDS: number = 5_000;

let articles: LocalForage;
let authors: LocalForage;
let bonusReports: LocalForage;
let categories: LocalForage;
let courses: LocalForage;
let industrySectors: LocalForage;
let products: LocalForage;
let sectors: LocalForage;
let sectorHistory: LocalForage;
let simplePages: LocalForage;
let syncState: LocalForage;
let tags: LocalForage;
let tickers: LocalForage;
let tradeAlerts: LocalForage;
let webinars: LocalForage;

/**
 * Memoized readiness. `initialize()` is called from the app plugin, from the sync worker and
 * defensively from half a dozen coordinators; without this, every one of those calls re-created
 * all fifteen instances.
 */
let initialization: Promise< void > | null = null;

function createStore( storeName: string, description: string ): LocalForage {
    return localforage.createInstance( {
        driver: localforage.INDEXEDDB,
        name: DATABASE_NAME,
        version: DATABASE_VERSION,
        storeName, // Should be alphanumeric, with underscores.
        description
    } );
}

function createStores(): void {
    articles = createStore( 'articles', 'The collection of all subscription-based articles' );
    authors = createStore( 'authors', 'The collection of all authors' );
    bonusReports = createStore( 'reports', 'The collection of all product-based bonus reports' );
    categories = createStore( 'categories', 'The collection of all categories' );
    courses = createStore( 'courses', 'The collection of all educational courses' );
    industrySectors = createStore( 'industry', 'The collection of all industry sectors' );
    products = createStore( 'products', 'The collection of all defined product offerings' );
    sectors = createStore( 'sectors', 'The collection of all configured sectors' );
    sectorHistory = createStore( 'history', 'The collection of all sector stats' );
    simplePages = createStore( 'pages', 'The collection of all non-interactive html page content items' );
    syncState = createStore( 'sync_state', 'The collection of all current syncing metrics' );
    tags = createStore( 'tags', 'The collection of all tags' );
    tickers = createStore( 'tickers', 'The collection of all searchable tickers' );
    tradeAlerts = createStore( 'alerts', 'The collection of all trade alerts authorized for user' );
    webinars = createStore( 'webinars', 'The collection of all webinars authorized for user' );
}

/**
 * Content every visitor sees identically. Safe to keep across a change of signed-in user.
 */
function publicContentStores(): LocalForage[] {
    return [ authors, categories, industrySectors, products, sectors, simplePages, tags, tickers ];
}

/**
 * Content the server filters by the caller's entitlements. Must not survive into another user's
 * session — see `clearUserScopedCaches`.
 */
function userScopedContentStores(): LocalForage[] {
    return [ articles, bonusReports, courses, sectorHistory, tradeAlerts, webinars ];
}

function allStores(): LocalForage[] {
    return [ ...publicContentStores(), ...userScopedContentStores(), syncState ];
}

function withTimeout< T >( work: Promise< T >, timeoutInMilliseconds: number ): Promise< T | undefined > {
    return Promise.race( [
        work,
        new Promise< undefined >( resolve => setTimeout( () => resolve( undefined ), timeoutInMilliseconds ) )
    ] );
}

/**
 * Resolve which of the legacy databases actually exist on this browser, so a first-time visitor
 * pays nothing. Falls back to the full list when `indexedDB.databases()` is unavailable.
 */
async function resolveLegacyDatabasesPresent(): Promise< readonly string[] > {
    if ( typeof indexedDB === 'undefined' || typeof indexedDB.databases !== 'function' ) {
        return LEGACY_DATABASE_NAMES;
    }

    try {
        const present = await indexedDB.databases();
        const names = new Set(
            present.map( info => info.name ).filter( ( name ): name is string => !!name )
        );
        return LEGACY_DATABASE_NAMES.filter( name => names.has( name ) );
    } catch {
        return LEGACY_DATABASE_NAMES;
    }
}

/**
 * Drop the abandoned pre-freeze databases exactly once per browser, sequentially, off the boot
 * critical path. If any drop is blocked by another tab the flag is not written, so the purge is
 * retried on a later load rather than leaving a half-finished state behind.
 */
async function purgeLegacyDatabases(): Promise< void > {
    // localStorage does not exist in a Worker. The main thread owns this cleanup; the worker must
    // never start a second, competing round of deletes.
    if ( typeof localStorage === 'undefined' ) {
        return;
    }

    if ( localStorage.getItem( legacyPurgeFlagKey ) === LEGACY_PURGE_GENERATION ) {
        return;
    }

    const stale = await resolveLegacyDatabasesPresent();
    if ( 0 === stale.length ) {
        localStorage.setItem( legacyPurgeFlagKey, LEGACY_PURGE_GENERATION );
        return;
    }

    logger.info( `[ localforage ] purging ${ stale.length } legacy database(s)` );

    let allDropped = true;
    for ( const name of stale ) {
        try {
            const dropped = await withTimeout(
                localforage.dropInstance( { name } ).then( () => true ),
                DROP_TIMEOUT_IN_MILLISECONDS
            );

            if ( !dropped ) {
                allDropped = false;
                logger.warn( `[ localforage ] legacy database "${ name }" is blocked — will retry on a later load` );
            }
        } catch ( err: any ) {
            allDropped = false;
            logger.warn( `[ localforage ] failed to drop legacy database "${ name }": ${ err?.message }` );
        }
    }

    if ( allDropped ) {
        localStorage.setItem( legacyPurgeFlagKey, LEGACY_PURGE_GENERATION );
    }
}

/**
 * The replacement for "rename the database and hope". Compares the schema version recorded inside
 * the database against the one this build expects, and reconciles the difference.
 */
async function applySchemaMigrations(): Promise< void > {
    let recordedVersion: number | null = null;
    try {
        recordedVersion = await syncState.getItem< number >( cacheSchemaVersionKey );
    } catch ( err: any ) {
        logger.warn( `[ localforage ] unable to read the cache schema version: ${ err?.message }` );
        return;
    }

    if ( recordedVersion === CACHE_SCHEMA_VERSION ) {
        return;
    }

    // No marker at all means a cache written before versioning existed. Its records are still
    // readable by this build, so adopt it in place — clearing here would be one more gratuitous
    // "reset your data" for every existing user, which is the whole thing this is meant to stop.
    if ( null == recordedVersion ) {
        await syncState.setItem( cacheSchemaVersionKey, CACHE_SCHEMA_VERSION );
        return;
    }

    logger.info(
        `[ localforage ] cache schema ${ recordedVersion } -> ${ CACHE_SCHEMA_VERSION }; clearing cached content`
    );

    // Cached records were written in a shape this build cannot trust. Clear the content stores —
    // the sync coordinators repopulate them — but keep the database, the session and the settings.
    const results = await Promise.allSettled(
        [ ...publicContentStores(), ...userScopedContentStores() ].map( store => store.clear() )
    );
    results.forEach( result => {
        if ( 'rejected' === result.status ) {
            logger.warn( `[ localforage ] schema migration could not clear a store: ${ result.reason }` );
        }
    } );

    await syncState.setItem( cacheSchemaVersionKey, CACHE_SCHEMA_VERSION );
}

/**
 * Drop every cached record while leaving the database itself in place.
 *
 * Deliberately does NOT call `dropInstance` on the live database: fifteen instances hold open
 * connections to it, so `deleteDatabase` blocks, and re-creating the instances afterwards races a
 * delete that may still be pending. Clearing each object store reaches the same end state without
 * the hazard.
 */
export async function resetCurrentCachedData(): Promise< void > {
    await LocalForagePlugin.initialize();

    const results = await Promise.allSettled( allStores().map( store => store.clear() ) );
    results.forEach( result => {
        if ( 'rejected' === result.status ) {
            logger.warn( `[ localforage ] reset could not clear a store: ${ result.reason }` );
        }
    } );

    if ( typeof localStorage !== 'undefined' ) {
        localStorage.clear();
    }

    // syncState was just cleared along with everything else — re-stamp the schema marker so the
    // next load treats this as a current, empty cache rather than a pre-versioning one.
    await syncState.setItem( cacheSchemaVersionKey, CACHE_SCHEMA_VERSION );
}

/**
 * Drop only the entitlement-filtered stores, keeping public content and the user's session.
 * Used when the cache changes hands — see `sync-supervisor`.
 *
 * Unlike the best-effort resets above, this one REPORTS failure. It is the only function here
 * that is a security boundary: its caller records the new owner on the strength of it, and a
 * clear that silently failed would leave the previous user's entitlement-filtered content in
 * place, relabelled as belonging to the incoming user and never reconsidered.
 *
 * @returns true only if every user-scoped store was emptied.
 */
export async function clearUserScopedCaches(): Promise< boolean > {
    // Unlike the reset paths, this can be reached without the plugin having settled — if an
    // earlier initialize() rejected, the bindings are assigned but not ready.
    await LocalForagePlugin.initialize();

    const results = await Promise.allSettled( userScopedContentStores().map( store => store.clear() ) );

    let cleared = true;
    results.forEach( result => {
        if ( 'rejected' === result.status ) {
            cleared = false;
            logger.error( `[ localforage ] could not clear a user-scoped store: ${ result.reason }` );
        }
    } );

    return cleared;
}

const LocalForagePlugin = {
    install( _: App< Element > ) {
        void this.initialize().catch( ( err: any ) =>
            logger.error( `[ localforage ] initialization failed: ${ err?.message }` )
        );
    },

    initialize(): Promise< void > {
        if ( initialization ) {
            return initialization;
        }

        // Instances are created synchronously so the exported bindings are usable immediately;
        // only readiness is awaited. Callers that guard on `typeof syncState === 'undefined'`
        // keep working unchanged.
        createStores();

        initialization = Promise.all( [
            articles.ready(),
            authors.ready(),
            bonusReports.ready(),
            categories.ready(),
            courses.ready(),
            industrySectors.ready(),
            products.ready(),
            sectors.ready(),
            sectorHistory.ready(),
            simplePages.ready(),
            syncState.ready(),
            tags.ready(),
            tickers.ready(),
            tradeAlerts.ready(),
            webinars.ready()
        ] )
            .then( () => applySchemaMigrations() )
            .then( () => {
                // Deliberately not awaited: legacy cleanup must never delay the first render.
                void purgeLegacyDatabases().catch( ( err: any ) =>
                    logger.warn( `[ localforage ] legacy purge failed: ${ err?.message }` )
                );
            } )
            .catch( ( err: any ) => {
                // Allow a later call to retry rather than memoizing the failure forever.
                initialization = null;
                throw err;
            } );

        return initialization;
    }
}

export default LocalForagePlugin;
export {
    articles,
    authors,
    bonusReports,
    categories,
    courses,
    industrySectors,
    products,
    sectors,
    sectorHistory,
    simplePages,
    syncState,
    tags,
    tickers,
    tradeAlerts,
    webinars
};
